import { getDisplayMeasurement, getDisplayAreaMeasurement, calculateDistance } from './drawingUtils';
import { TOOLS } from '../constants/drawingTools';
import { calculateTotalAreaWithHoles } from './booleanOperations';

/**
 * Get measurement data for a shape using PRE-CALCULATED values from the shape object.
 * Falls back to calculating from points if pre-calculated values don't exist.
 * This function uses segmentDistances, totalDistance, area, etc. that are already
 * stored in the shape object from Firestore, avoiding expensive recalculations when possible.
 * 
 * @param {Object} shape - The shape object with optional pre-calculated measurement values
 * @param {Object} currentScaleProps - Scale properties for display conversion
 * @param {number} dpi - Document DPI for display conversion
 * @param {Object} imageSize - Image dimensions for display conversion
 * @param {string} overrideSystem - System to format the display measurement (metric vs imperial)
 * @returns {Object} - { summary, details, rawValue, rawUnit }
 */
export const getShapeMeasurementData = (shape, currentScaleProps, dpi, imageSize, overrideSystem = null) => {
    if (!shape || !shape.type) return { summary: '', details: [], rawValue: 0, rawUnit: 'u' };
    
    switch (shape.type) {
        case TOOLS.MEASURE:
        case TOOLS.COTATION: {
            // Calculate distance if not present
            let distance = shape.distance;
            if (distance === undefined && shape.points && shape.points.length >= 4) {
                distance = calculateDistance(
                    shape.points[0], shape.points[1],
                    shape.points[2], shape.points[3]
                );
            }
            
            if (distance !== undefined && distance > 0) {
                const measurement = getDisplayMeasurement(distance, currentScaleProps, dpi, imageSize, overrideSystem);
                return { summary: `${measurement.value} ${measurement.unit}`, details: [], rawValue: distance, rawUnit: 'px' };
            }
            return { summary: 'No measurement', details: [], rawValue: 0, rawUnit: 'u' };
        }
        
        case TOOLS.POLYLINE: {
            const details = [];
            let summary = 'No measurement';
            let rawValue = 0;
            
            // Calculate segmentDistances if not present
            let segmentDistances = shape.segmentDistances;
            if (!segmentDistances && shape.points && shape.points.length >= 4) {
                segmentDistances = [];
                for (let i = 0; i < shape.points.length - 2; i += 2) {
                    const dist = calculateDistance(
                        shape.points[i], shape.points[i + 1],
                        shape.points[i + 2], shape.points[i + 3]
                    );
                    segmentDistances.push(dist);
                }
            }
            
            // Use calculated or pre-calculated segmentDistances
            if (segmentDistances && segmentDistances.length > 0) {
                let totalDistance = 0;
                
                segmentDistances.forEach((dist, index) => {
                    const measurement = getDisplayMeasurement(dist, currentScaleProps, dpi, imageSize, overrideSystem);
                    details.push(`Segment ${index + 1}: ${measurement.value} ${measurement.unit}`);
                    totalDistance += dist;
                });
                
                // Use pre-calculated totalDistance if available, otherwise use sum
                const total = shape.totalDistance !== undefined ? shape.totalDistance : totalDistance;
                const totalMeasurement = getDisplayMeasurement(total, currentScaleProps, dpi, imageSize, overrideSystem);
                summary = `Total: ${totalMeasurement.value} ${totalMeasurement.unit}`;
                rawValue = total;
            }
            return { summary, details, rawValue, rawUnit: 'px' };
        }
        
        case TOOLS.AREA: {
            const details = [];
            let summary = 'No measurement';
            let rawValue = 0;
            let rawUnit = 'px^2'; // Area is in square pixels
            
            // Calculate segmentDistances if not present
            let segmentDistances = shape.segmentDistances;
            if (!segmentDistances && shape.points && shape.points.length >= 6) {
                segmentDistances = [];
                const numVertices = shape.points.length / 2;
                for (let i = 0; i < numVertices; i++) {
                    const nextIndex = (i + 1) % numVertices;
                    const dist = calculateDistance(
                        shape.points[i * 2], shape.points[i * 2 + 1],
                        shape.points[nextIndex * 2], shape.points[nextIndex * 2 + 1]
                    );
                    segmentDistances.push(dist);
                }
            }
            
            // Use calculated or pre-calculated segmentDistances for perimeter
            if (segmentDistances && segmentDistances.length > 0) {
                let totalPerimeter = 0;
                details.push('--- Perimeter ---');
                
                segmentDistances.forEach((dist, index) => {
                    const measurement = getDisplayMeasurement(dist, currentScaleProps, dpi, imageSize, overrideSystem);
                    details.push(`Side ${index + 1}: ${measurement.value} ${measurement.unit}`);
                    totalPerimeter += dist;
                });
                
                const perimeterMeasurement = getDisplayMeasurement(totalPerimeter, currentScaleProps, dpi, imageSize, overrideSystem);
                details.push(`Perimeter: ${perimeterMeasurement.value} ${perimeterMeasurement.unit}`);
                
                // Calculate area if not present - use calculateTotalAreaWithHoles to properly account for holes
                const area = shape.area !== undefined && shape.area > 0 
                    ? shape.area 
                    : (shape.points && shape.points.length >= 6 ? calculateTotalAreaWithHoles(shape.points, shape.holes) : 0);
                
                if (area > 0) {
                    const areaMeasurement = getDisplayAreaMeasurement(area, currentScaleProps, dpi, overrideSystem);
                    details.push('--- Area ---', `Main Area: ${areaMeasurement.value} ${areaMeasurement.unit}`);
                    summary = `Area: ${areaMeasurement.value} ${areaMeasurement.unit}`;
                    rawValue = area;
                    
                    // Handle holes using pre-calculated hole areas
                    if (shape.holes && shape.holes.length > 0) {
                        details.push('--- Holes ---');
                        let totalHolesArea = 0;
                        
                        shape.holes.forEach((hole, holeIndex) => {
                            // Use pre-calculated hole area if available
                            const holeArea = hole.area !== undefined ? hole.area : 0;
                            totalHolesArea += holeArea;
                            const holeAreaMeasurement = getDisplayAreaMeasurement(holeArea, currentScaleProps, dpi, overrideSystem);
                            details.push(`Hole ${holeIndex + 1}: ${holeAreaMeasurement.value} ${holeAreaMeasurement.unit}`);
                        });
                        
                        if (totalHolesArea > 0) {
                            const totalHolesMeasurement = getDisplayAreaMeasurement(totalHolesArea, currentScaleProps, dpi, overrideSystem);
                            details.push(`Total Holes Area: ${totalHolesMeasurement.value} ${totalHolesMeasurement.unit}`);
                            
                            // Use pre-calculated netArea if available, otherwise compute
                            const netArea = shape.netArea !== undefined ? shape.netArea : (area - totalHolesArea);
                            if (netArea > 0) {
                                const netAreaMeasurement = getDisplayAreaMeasurement(netArea, currentScaleProps, dpi, overrideSystem);
                                details.push(`Net Area: ${netAreaMeasurement.value} ${netAreaMeasurement.unit}`);
                                summary = `Net Area: ${netAreaMeasurement.value} ${netAreaMeasurement.unit}`;
                                rawValue = netArea;
                            }
                        }
                    }
                }
            }
            return { summary, details, rawValue, rawUnit };
        }
        
        case TOOLS.LINE:
        case TOOLS.ARROW: {
            // Calculate distance if not present
            let distance = shape.distance;
            if (distance === undefined && shape.points && shape.points.length >= 4) {
                distance = calculateDistance(
                    shape.points[0], shape.points[1],
                    shape.points[2], shape.points[3]
                );
            }
            
            if (distance !== undefined && distance > 0) {
                const measurement = getDisplayMeasurement(distance, currentScaleProps, dpi, imageSize, overrideSystem);
                return { summary: `Length: ${measurement.value} ${measurement.unit}`, details: [], rawValue: distance, rawUnit: 'px' };
            }
            return { summary: 'No measurement', details: [], rawValue: 0, rawUnit: 'u' };
        }
        
        case TOOLS.POINT: {
            const details = [];
            let summary = 'Point';
            // Use pre-calculated radius from shape object
            const radius = shape.radius || 0;
            const radiusMeasurement = getDisplayMeasurement(radius, currentScaleProps, dpi, imageSize, overrideSystem);
            details.push(`--- Point Properties ---`);
            details.push(`Position: X: ${Math.round(shape.x || 0)}, Y: ${Math.round(shape.y || 0)}`);
            details.push(`Radius: ${radiusMeasurement.value} ${radiusMeasurement.unit}`);
            
            // Use pre-calculated diameter if available, otherwise compute from radius
            const diameter = shape.diameter !== undefined ? shape.diameter : (radius * 2);
            const diameterMeasurement = getDisplayMeasurement(diameter, currentScaleProps, dpi, imageSize, overrideSystem);
            details.push(`Diameter: ${diameterMeasurement.value} ${diameterMeasurement.unit}`);
            
            // Use pre-calculated circleArea if available
            const circleArea = shape.circleArea !== undefined ? shape.circleArea : (Math.PI * radius * radius);
            const areaMeasurement = getDisplayAreaMeasurement(circleArea, currentScaleProps, dpi, overrideSystem);
            details.push(`Circle Area: ${areaMeasurement.value} ${areaMeasurement.unit}`);
            
            if (shape.rotation !== undefined) {
                details.push(`Rotation: ${shape.rotation}°`);
            }
            summary = `Radius: ${radiusMeasurement.value} ${radiusMeasurement.unit}`;
            return { summary, details, rawValue: radius, rawUnit: 'px' };
        }
        
        default:
            return { summary: '', details: [], rawValue: 0, rawUnit: 'u' };
    }
};